/**
 * Lane C0-FACE (lab only): the crispness pass on the proposed faces' traced eyes and brows (fine.ts), asked for by
 * the user on 2026-10-10 (clean 1-px lines, no muddy pixels):
 * - every pixel of a traced feature is given a role (lash, iris, pupil, eye white, white in shade, shine, lid crease,
 *   brow hair, brow hair in shade, nose / mouth shade) and painted in that role's one colour, so the soft in-between
 *   tones left by the tracing (skin-to-lash, white-to-iris, brow-to-skin) become clean edges;
 * - skin-coloured fringe pixels (the tracing's anti-alias against the head) are dropped (the head's skin shows);
 * - stray pixels (no neighbour of their own kind) are removed, except shines and pupils;
 * - head-on, the right-hand feature is mirrored to the left so both sides match exactly (the centre line lies
 *   between canvas x −1 and 0);
 * - one ink for every lash and lid line.
 * Pixels stay on the canvas grid the live face uses (two canvas pixels per art pixel); nothing is moved.
 */
import type { View } from '../../sprites';
import { shade } from '../../palette';
import { type FineSprite, REF_HEAD } from '../head2';
import { CREASE, LASH } from './fine';

const KEYS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export const hex = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
export function mix(a: string, b: string, k: number): string {
  const A = hex(a);
  const B = hex(b);
  return `#${A.map((v, i) => Math.round(v + (B[i]! - v) * k).toString(16).padStart(2, '0')).join('')}`;
}
const lumOf = (c: number[]) => c[0]! * 0.3 + c[1]! * 0.59 + c[2]! * 0.11;
const chroma = (c: number[]) => Math.max(...c) - Math.min(...c);
const skinLike = (c: number[]) => c[0]! > c[1]! && c[1]! > c[2]! && c[0]! - c[2]! > 25 && lumOf(c) >= 95;

/** Roles of a feature's pixels. */
export type Role = 'lash' | 'iris' | 'irisL' | 'pupil' | 'white' | 'whiteS' | 'shine' | 'crease' | 'hair' | 'hairS' | 'shadeA' | 'shadeB';

export interface CrispPx {
  x: number;
  y: number;
  role: Role;
}

/** The traced head's main skin tone in a view (its commonest colour). */
function mainTone(view: View): number[] {
  const sp = REF_HEAD[view] ?? REF_HEAD.front!;
  const count = new Map<string, number>();
  for (const row of sp.rows) for (const ch of row) if (ch !== '.') count.set(ch, (count.get(ch) ?? 0) + 1);
  const ch = [...count].sort((a, b) => b[1] - a[1])[0]![0];
  return hex(sp.palette[KEYS.indexOf(ch)]!);
}

function rolesOf(sp: FineSprite, slot: 'eyes' | 'closed' | 'brows' | 'nose' | 'mouth', view: View): CrispPx[] {
  const skin = lumOf(mainTone(view));
  const out: CrispPx[] = [];
  const lums: number[] = [];
  sp.rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      const ch = row[c]!;
      if (ch === '.') continue;
      if (ch === LASH || ch === CREASE) {
        out.push({ x: sp.x + c, y: sp.y + r, role: ch === LASH ? (slot === 'brows' ? 'hair' : 'lash') : 'crease' });
        lums.push(ch === LASH ? 0 : 255);
        continue;
      }
      const col = hex(sp.palette[KEYS.indexOf(ch)]!);
      const l = lumOf(col);
      const x = sp.x + c;
      const y = sp.y + r;
      let role: Role | undefined;
      if (slot === 'closed') role = l < 120 ? 'lash' : undefined;
      else if (slot === 'eyes') {
        if (l < 52) role = 'pupil';
        else if (l >= 235) role = 'shine';
        else if (chroma(col) < 40 && l >= 120) role = l >= 200 ? 'white' : 'whiteS';
        else if (skinLike(col)) role = l < skin * 0.82 ? 'crease' : undefined;
        else role = 'iris';
      } else if (slot === 'brows') {
        // Only near-skin fringe goes; the brow's tapered ends stay as its lighter tone.
        if (skinLike(col) && l > skin * 0.92) role = undefined;
        else role = l < 80 ? 'hair' : 'hairS';
      } else {
        // Nose and mouth: two shades, the darker the line.
        role = l < skin * 0.86 ? 'shadeA' : l < skin * 0.97 ? 'shadeB' : undefined;
      }
      if (role) {
        out.push({ x, y, role });
        lums.push(l);
      }
    }
  });
  if (slot === 'eyes') return eyeRoles(out, lums);
  return out;
}

/**
 * Eyes, second pass: dark pixels running down from the top of a column (and the corners) are the lash; dark pixels
 * inside are the pupil, but only the darkest few (a pupil about 2 x 2); the iris splits into its colour and a lit
 * lower part (the brightest third), so the iris keeps its colour instead of turning to ink.
 */
function eyeRoles(px: CrispPx[], lums: number[]): CrispPx[] {
  const cols = new Map<number, number[]>();
  px.forEach((p, i) => cols.set(p.x, [...(cols.get(p.x) ?? []), i]));
  for (const idx of cols.values()) {
    idx.sort((a, b) => px[a]!.y - px[b]!.y);
    // The top pixel of a column is the lid line whatever its traced tone (unless it is the eye's white or shine).
    const t = idx[0]!;
    if (px[t]!.role === 'iris' || px[t]!.role === 'crease') px[t] = { ...px[t]!, role: 'lash' };
    // At most two rows of lash (three where a heavier lash was added); the dark below them is the iris's top.
    const most = idx.filter((i) => lums[i] === 0).length > 0 ? 3 : 2;
    let n = 0;
    for (const i of idx) {
      if ((px[i]!.role !== 'pupil' && px[i]!.role !== 'lash') || n >= most) break;
      px[i] = { ...px[i]!, role: 'lash' };
      n++;
    }
    // A column that is all dark (an eye corner) is lash.
    if (idx.every((i) => px[i]!.role === 'pupil' || px[i]!.role === 'lash')) for (const i of idx) px[i] = { ...px[i]!, role: 'lash' };
  }
  const iris = px.map((_, i) => i).filter((i) => px[i]!.role === 'iris' || px[i]!.role === 'pupil');
  if (iris.length) {
    const sorted = [...iris].sort((a, b) => lums[a]! - lums[b]!);
    const nP = Math.min(4, Math.max(1, Math.round(iris.length * 0.22)));
    const nL = Math.round(iris.length * 0.33);
    sorted.forEach((i, k) => {
      px[i] = { ...px[i]!, role: k < nP ? 'pupil' : k >= sorted.length - nL ? 'irisL' : 'iris' };
    });
  }
  return px;
}

/** Drops pixels with no neighbour (8-way) of a related role; shines and pupils stay. */
function clean(px: CrispPx[]): CrispPx[] {
  const at = new Map(px.map((p) => [`${p.x},${p.y}`, p]));
  const family = (r: Role) =>
    r === 'lash' || r === 'crease' ? 'line' : r === 'hair' || r === 'hairS' ? 'hair' : r === 'shadeA' || r === 'shadeB' ? 'shade' : 'eye';
  return px.filter((p) => {
    if (p.role === 'shine' || p.role === 'pupil' || p.role === 'iris' || p.role === 'irisL') return true;
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const q = at.get(`${p.x + dx},${p.y + dy}`);
        if (q && family(q.role) === family(p.role)) return true;
        // A lash pixel touching the eye it lines is not stray.
        if (q && p.role === 'lash' && family(q.role) === 'eye') return true;
      }
    return false;
  });
}

/** Head-on: the screen-right half mirrored onto the left (x → −1 − x). */
function symmetric(px: CrispPx[]): CrispPx[] {
  const right = px.filter((p) => p.x >= 0);
  return [...right, ...right.map((p) => ({ ...p, x: -1 - p.x }))];
}


/** A traced feature sprite, sharpened: its pixels with roles, cleaned, two-tone brows, symmetric head-on. */
export function crispPixels(sp: FineSprite, slot: 'eyes' | 'closed' | 'brows' | 'nose' | 'mouth', view: View): CrispPx[] {
  let px = clean(rolesOf(sp, slot, view));
  // Nose and mouth sit on the centre line already; mirroring would widen them.
  if (view === 'front' && slot !== 'nose' && slot !== 'mouth') px = symmetric(px);
  return px;
}

/** The one ink for lashes and lid lines (the figure outline's deep plum, a step warmer). */
export const INK = '#1e1218';

/** Colours of the roles for one person. */
export function roleColours(o: { skin: string; skinS: string; iris: string; sclera: string; brow: string }): Record<Role, string> {
  return {
    lash: INK,
    iris: lumOf(hex(o.iris)) < 55 ? shade(o.iris, 0.1) : o.iris,
    irisL: shade(o.iris, lumOf(hex(o.iris)) < 55 ? 0.22 : 0.14),
    pupil: mix(shade(o.iris, -0.22), INK, 0.45),
    white: o.sclera,
    whiteS: mix(o.sclera, o.skinS, 0.38),
    shine: '#ffffff',
    crease: mix(o.skin, o.skinS, 0.75),
    hair: o.brow,
    hairS: mix(o.brow, o.skinS, 0.4),
    shadeA: mix(o.skinS, '#8a4a3e', 0.25),
    shadeB: mix(o.skin, o.skinS, 0.55),
  };
}
